import axios from 'axios'
import { Service } from 'axios-middleware'

import { getServiceURL, getURL } from '@/utils'
import { getServiceURLserverSide } from '@infrastructure/Urls'
import Toggles from '@domain/toggles/Toggles'
import authService from '@/services/auth.service'
import langCodeInterceptor from './langCodeInterceptor.util'
import { LOGOUT } from '../redux/actions/auth.actions'
import * as Sentry from "@sentry/react";

let isRefreshingToken = false

async function waitUntilNewToken(config) {
	return await new Promise(resolve => {
		const interval = setInterval(() => {
			if (!isRefreshingToken) {
				resolve(config)
				clearInterval(interval)
			}
		}, 300)
	})
}

export const authClient = axios.create({
	timeout: 150000,
	withCredentials: true
})

langCodeInterceptor(authClient)

authClient.interceptors.response.use(
	(response) => response,
	(error) => {
		Sentry.addBreadcrumb({
			category: "error",
			message: error.config.url,
			level: "error",
		});
		Sentry.captureException(error);
		return Promise.reject(error);
	},
);

// Refresh URL must follow the same toggle as `UserApi.refreshUser` and
// `auth.service.refreshToken`. Otherwise some flows refresh through v1 and
// re-emit `vibia_refresh` scoped to `path=/users/v1/auth/refresh`, which
// the browser no longer sends to `/v3/auth/refresh`, leaving v3 to answer
// 401 "No token found" on the next page reload (DV-5930).
const getRefreshTokenUrl = () => {
	const useV3 = Toggles.isEnabled('authenticateUserV3')
	return useV3
		? getURL('/v3/auth/refresh', 'api')
		: `${getServiceURL('users')}/v1/auth/refresh`
}

export function authMiddleware(logout) {

	const service = new Service(authClient)

	const accountUrl = `${getServiceURL('users')}/v1/account`
	const refreshTokenUrl = getRefreshTokenUrl()
	const logoutUrl = Toggles.isEnabled('logoutv3')
		? getServiceURLserverSide('v3') + '/logout'
		: `${getServiceURL('users')}/v1/auth/logout`

	const retryRequest = async (config) => {
		return authClient.request({
			...config,
			hasRetriedRequest: true
		})
	}

	service.register({

		async onRequest(config) {
			if ([refreshTokenUrl, accountUrl].includes(config.url)) {
				return config
			}

			if (isRefreshingToken) {
				return await waitUntilNewToken(config)	
			}

			return config
		},
		async onResponse(response) {
			if ([refreshTokenUrl, accountUrl].includes(response.config.url)) {
				isRefreshingToken = false
			}

			return response
		},
		async onResponseError(err) {
			if (err.response?.status !== 401) {
				throw err
			}

			if (err.config.hasRetriedRequest) {
				console.log('Request failed and has already been retried.')
				throw err
			}

			try {
				if (err.config.url === refreshTokenUrl) {
					console.log('Refresh token failed.')
					isRefreshingToken = false

					if(err.request.status === 401) throw err;

					const res = await logout()
					if (res.type === LOGOUT.FAILURE) {
						throw err
					}
					return err
				}

				if (err.config.url === logoutUrl) {
					throw err
				}

				if (!isRefreshingToken) {
					console.log('Refreshing token...')
					isRefreshingToken = true
					await authService.refreshToken()
					console.log('Token refreshed!')
					isRefreshingToken = false
					return await retryRequest(err.config)
				}

				await waitUntilNewToken(err.config)
				return await retryRequest(err.config)

			} catch (err) {
				isRefreshingToken = false
				console.log('Cannot refresh token.')
				throw err
			}
		}
	})
}